import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { TopNav, RoleBadge } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Upload, Link2, RefreshCw, AlertTriangle, FileSpreadsheet, CheckCircle2, Search } from "lucide-react";
import { warn } from "@/lib/logger";

const ROLES = ["P", "D", "C", "A"];

/**
 * Listone management for any room creator. The uploaded listone is shared app-wide:
 * all creators use the same players collection as source of truth for their auctions.
 */
export default function AdminListone() {
  const { user, ready } = useAuth();
  const [info, setInfo] = useState(null);
  const [file, setFile] = useState(null);
  const [replace, setReplace] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sheetUrl, setSheetUrl] = useState("");
  const [syncing, setSyncing] = useState(false);

  const [players, setPlayers] = useState([]);
  const [roleFilter, setRoleFilter] = useState("P");
  const [search, setSearch] = useState("");

  const isSuperadmin = user?.email === "admin@fantaasta.it";

  const loadInfo = useCallback(() => {
    api.get("/listone/info").then((r) => setInfo(r.data))
      .catch((err) => warn("listone info load failed:", err));
  }, []);
  const loadPlayers = useCallback(() => {
    api.get("/players", { params: { role: roleFilter, search: search || undefined } })
      .then((r) => setPlayers(r.data))
      .catch((err) => warn("players load failed:", err));
  }, [roleFilter, search]);
  useEffect(() => { loadInfo(); }, [loadInfo]);
  useEffect(() => { loadPlayers(); }, [loadPlayers]);

  const roleCounts = useMemo(() => {
    const counts = { P: 0, D: 0, C: 0, A: 0 };
    for (const p of players) if (ROLES.includes(p.role)) counts[p.role] += 1;
    return counts;
  }, [players]);

  if (!ready) return <div><TopNav /><div className="p-10 text-center text-slate-400">Caricamento…</div></div>;
  if (!user) return <Navigate to="/login" replace />;

  const uploadFile = async () => {
    if (!file) return toast.error("Seleziona un file CSV, Excel o PDF");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/players/import?replace=${replace}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Listone aggiornato: +${data.inserted} nuovi · ${data.updated} aggiornati · ${data.removed} rimossi · ${data.total} totali`);
      setFile(null);
      loadInfo();
      loadPlayers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  const syncFromUrl = async () => {
    if (!sheetUrl.trim()) return toast.error("Incolla l'URL del Google Sheet (pubblicato come CSV)");
    setSyncing(true);
    try {
      const { data } = await api.post("/listone/sync-url", { url: sheetUrl.trim(), replace });
      toast.success(`Sincronizzato: +${data.inserted} nuovi · ${data.updated} aggiornati · ${data.removed} rimossi`);
      loadInfo();
      loadPlayers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSyncing(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  const hasListone = (info?.count ?? 0) > 0;

  return (
    <div>
      <TopNav />
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="label-eyebrow mb-2">Listone — fonte dati calciatori</div>
        <h1 className="text-4xl font-black tracking-tight mb-2">Gestione Listone</h1>
        <p className="text-slate-400 mb-6">
          Carica qui il file ufficiale di <b className="text-emerald-400">Fantacalcio.it</b> (CSV con separatore <code>;</code> o Excel).
          Il listone è condiviso: tutte le sale d'asta pescano dai calciatori qui elencati.
          {!isSuperadmin && " Qualsiasi creatore di sala può caricare o aggiornare il listone."}
        </p>

        {/* Live status card */}
        <div className={`glass rounded-2xl p-5 mb-6 grid grid-cols-2 sm:grid-cols-4 gap-4 ${hasListone ? "" : "border-amber-500/40 bg-amber-500/5"}`}
             data-testid="listone-info">
          <div>
            <div className="label-eyebrow mb-1">Stato</div>
            <div className={`font-black flex items-center gap-1 ${hasListone ? "text-emerald-400" : "text-amber-400"}`}>
              {hasListone ? <><CheckCircle2 className="w-4 h-4" />Caricato</> : <><AlertTriangle className="w-4 h-4" />Non caricato</>}
            </div>
          </div>
          <div>
            <div className="label-eyebrow mb-1">Calciatori totali</div>
            <div className="font-mono font-black text-2xl text-emerald-400" data-testid="listone-total-count">
              {info?.count ?? 0}
            </div>
          </div>
          <div>
            <div className="label-eyebrow mb-1">Ultimo aggiornamento</div>
            <div className="font-bold text-sm">{fmtDate(info?.updated_at)}</div>
          </div>
          <div>
            <div className="label-eyebrow mb-1">Aggiornato da</div>
            <div className="font-bold text-sm truncate">{info?.updated_by || "—"}</div>
          </div>
        </div>

        {/* Upload card */}
        <div className="glass rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Upload className="w-5 h-5 text-emerald-400" />
            <h2 className="text-xl font-black">Carica file CSV, Excel o PDF</h2>
          </div>
          <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-xl p-4 mb-4 text-sm text-slate-300 leading-relaxed">
            <div className="font-bold text-emerald-400 mb-1">Compatibile con il file ufficiale di Fantacalcio.it</div>
            Supporta il formato con separatore <code className="text-emerald-400">;</code>, prima riga di titolo/banner
            (es. <i>"Quotazioni Fantacalcio Stagione 2024-25"</i>) e colonne <b>Nome</b>, <b>Ruolo</b>/<b>R</b>, <b>Squadra</b>,
            <b> Qt.A</b>, <b>Qt.I</b>, <b>Fvm</b>. Il sistema <b>rileva automaticamente il separatore</b> e
            <b> salta le righe banner iniziali</b> cercando l'header reale. Stesso comportamento per <code>.xlsx</code>.
          </div>
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
            <div className="flex-1 w-full">
              <Label className="text-slate-300">File (.csv, .xlsx, .pdf)</Label>
              <Input data-testid="listone-file-input" type="file" accept=".csv,.xlsx,.pdf"
                     onChange={(e) => setFile(e.target.files?.[0])}
                     className="mt-2 bg-slate-900/60 border-white/10 h-11 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-emerald-500 file:text-slate-950 file:font-bold" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-300 h-11 whitespace-nowrap" data-testid="listone-replace-label">
              <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)}
                     data-testid="listone-replace" className="w-4 h-4 accent-emerald-500" />
              Sostituisci tutto
            </label>
            <Button data-testid="listone-upload-btn" onClick={uploadFile} disabled={!file || uploading}
                    className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold h-11 px-6">
              <Upload className="w-4 h-4 mr-2" />{uploading ? "Caricamento…" : "Carica Listone"}
            </Button>
          </div>
          {replace && (
            <div className="mt-3 flex items-start gap-2 text-xs text-amber-400 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                Modalità <b>sostituzione totale</b>: i calciatori non presenti nel nuovo file vengono rimossi dal listone.
                Gli ID esistenti sono preservati per (Nome + Squadra) per non rompere le sale attive.
              </div>
            </div>
          )}
        </div>

        {/* URL sync (optional) */}
        <div className="glass rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Link2 className="w-5 h-5 text-emerald-400" />
            <h2 className="text-lg font-black">Sincronizza da Google Sheet (opzionale)</h2>
          </div>
          <p className="text-sm text-slate-400 mb-4">
            In Google Sheets: <b>File → Condividi → Pubblica sul web → CSV</b> → copia il link e incollalo qui.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input data-testid="listone-url-input" placeholder="https://docs.google.com/spreadsheets/d/.../pub?output=csv"
                   value={sheetUrl} onChange={(e) => setSheetUrl(e.target.value)}
                   className="bg-slate-900/60 border-white/10 h-11 font-mono text-xs" />
            <Button data-testid="listone-sync-btn" onClick={syncFromUrl} disabled={!sheetUrl.trim() || syncing}
                    className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold h-11 px-6">
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />{syncing ? "Sync…" : "Sincronizza"}
            </Button>
          </div>
        </div>

        {/* Players list — only visible when listone is non-empty */}
        {hasListone ? (
          <div className="glass rounded-2xl p-6" data-testid="listone-players-list">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <div className="label-eyebrow mb-1">Lista calciatori importati</div>
                <h2 className="text-xl font-black">
                  Totale: <span className="text-emerald-400">{info?.count ?? 0}</span>
                  <span className="text-sm text-slate-400 font-normal ml-3">
                    (P {roleCounts.P} · D {roleCounts.D} · C {roleCounts.C} · A {roleCounts.A} filtrati)
                  </span>
                </h2>
              </div>
            </div>
            <Tabs value={roleFilter} onValueChange={setRoleFilter} className="mb-3">
              <TabsList className="bg-slate-900/60 border border-white/10">
                {ROLES.map((r) => (
                  <TabsTrigger key={r} value={r} data-testid={`listone-filter-${r}`}
                               className="data-[state=active]:bg-emerald-500 data-[state=active]:text-slate-950 font-bold">
                    {r}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <div className="relative mb-3">
              <Search className="w-4 h-4 absolute left-3 top-3.5 text-slate-500" />
              <Input data-testid="listone-search" placeholder="Cerca calciatore o squadra…"
                     value={search} onChange={(e) => setSearch(e.target.value)}
                     className="pl-9 bg-slate-900/60 border-white/10 h-11" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[500px] overflow-y-auto scroll-thin pr-1">
              {players.map((p) => (
                <div key={p.id}
                     data-testid={`listone-player-${p.id}`}
                     className="flex items-center gap-2 p-2 rounded-lg bg-slate-900/50 border border-white/5">
                  <RoleBadge role={p.role} className="w-9 h-9 text-xs" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold truncate">{p.name}</div>
                    <div className="text-[10px] text-slate-400 truncate">{p.team}</div>
                  </div>
                  <div className="font-mono text-sm font-bold text-emerald-400">{p.quotation}</div>
                </div>
              ))}
              {players.length === 0 && (
                <div className="col-span-full text-center text-slate-500 text-sm py-6 italic">
                  Nessun risultato con questi filtri.
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="glass rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <FileSpreadsheet className="w-5 h-5 text-slate-400" />
              <h2 className="text-lg font-bold">Formato di riferimento</h2>
            </div>
            <pre className="text-xs bg-slate-900/70 border border-white/5 rounded-lg p-3 overflow-x-auto scroll-thin font-mono text-slate-300">{`Nome;Ruolo;Squadra;Qt.A;Qt.I;Fvm
Maignan;P;Milan;22;20;80
Bastoni;D;Inter;24;22;95
Barella;C;Inter;30;28;110
Osimhen;A;Napoli;32;30;120`}</pre>
            <p className="text-xs text-slate-500 mt-2">
              Il separatore CSV e la riga di intestazione reale sono rilevati automaticamente.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
